<template>
  <!-- The printable counter sign: the brand-coloured card holds the business
       logo, an editable headline, the QR code customers scan, and a fixed
       "Powered by Lulu Rewards" footer. Colours are inherited from the
       branding step. The QR code pattern here is a decorative mock - the real
       scannable code is rendered server-side and emailed once setup
       completes. -->
  <div class="counter-sign-wrap">
    <div class="counter-sign" :style="{ backgroundColor: cardBackgroundColor }">
      <!-- Logo -->
      <div v-if="showLogo" class="qr-logo-box">
        <img
          v-if="logoUrl"
          :src="logoUrl"
          alt="Business logo"
          class="qr-logo-img"
        />
        <span v-else class="qr-logo-placeholder" :style="{ color: textColor }">
          LOGO
        </span>
      </div>

      <!-- Headline -->
      <div class="counter-sign-headline" :style="{ color: textColor }">
        {{ headline || 'Join our rewards program!' }}
      </div>

      <!-- QR code (white box, always black on white for scannability) -->
      <div class="qr-box">
        <svg
          class="qr-svg"
          viewBox="0 0 21 21"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <g fill="#000">
            <rect x="0" y="0" width="7" height="7" />
            <rect x="14" y="0" width="7" height="7" />
            <rect x="0" y="14" width="7" height="7" />
          </g>
          <g fill="#fff">
            <rect x="1" y="1" width="5" height="5" />
            <rect x="15" y="1" width="5" height="5" />
            <rect x="1" y="15" width="5" height="5" />
          </g>
          <g fill="#000">
            <rect x="2" y="2" width="3" height="3" />
            <rect x="16" y="2" width="3" height="3" />
            <rect x="2" y="16" width="3" height="3" />
          </g>
          <g fill="#000">
            <rect
              v-for="(cell, idx) in qrCells"
              :key="idx"
              :x="cell.x"
              :y="cell.y"
              width="1"
              height="1"
            />
          </g>
        </svg>
      </div>

      <!-- Fixed footer -->
      <div class="counter-sign-footer" :style="{ color: textColor }">
        Powered by Lulu Rewards
        <img :src="luluLogo" alt="Lulu Rewards" class="qr-footer-logo" />
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import luluLogo from '@/assets/logo.png';

export default {
  name: 'QrCodePreview',
  props: {
    headline: { type: String, default: 'Join our rewards program!' },
    logoUrl: { type: String, default: null },
    showLogo: { type: Boolean, default: true },
    cardBackgroundColor: { type: String, default: '#7e8bce' },
    textColor: { type: String, default: '#ffffff' },
  },
  setup() {
    // Decorative QR code data cells - deterministic so the preview is stable.
    const qrCells = computed(() => {
      const cells = [];
      const inFinder = (x, y) =>
        (x <= 7 && y <= 7) || (x >= 13 && y <= 7) || (x <= 7 && y >= 13);
      for (let y = 0; y < 21; y++) {
        for (let x = 0; x < 21; x++) {
          if (inFinder(x, y)) continue;
          const hash = (x * 53 + y * 31 + x * y * 7) % 100;
          if (hash < 47) cells.push({ x, y });
        }
      }
      return cells;
    });

    return { qrCells, luluLogo };
  },
};
</script>

<style scoped>
.counter-sign-wrap {
  width: 280px;
  margin: 0 auto;
}

/* A plain box-shadow rather than a drop-shadow filter, which Safari can
   render as a hard offset copy. Matches the rewards card preview. */
.counter-sign {
  width: 280px;
  box-sizing: border-box;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  padding: 26px 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
}

.qr-logo-box {
  width: 120px;
  height: 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  box-sizing: border-box;
}

.qr-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.qr-logo-placeholder {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.04em;
  opacity: 0.85;
}

.counter-sign-headline {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  margin-bottom: 18px;
  word-break: break-word;
}

.qr-box {
  width: 196px;
  height: 196px;
  background: #ffffff;
  border-radius: 6px;
  padding: 10px;
  box-sizing: border-box;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.qr-svg {
  width: 100%;
  height: 100%;
  display: block;
  shape-rendering: crispEdges;
}

.counter-sign-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  font-size: 11px;
  font-weight: 700;
  opacity: 0.95;
}

.qr-footer-logo {
  width: 18px;
  height: 18px;
  object-fit: contain;
}
</style>
